import { useCallback, useEffect, useRef } from 'react';
import { useCreatorContext } from 'controllers/business/Creator/Creator.controller';
import { StreamEntityType } from 'types/enums';
import { useActivePlayerContext } from 'contexts/ActivePlayerContext/ActivePlayerContext';
import { useExecutionEnvironmentContext } from 'contexts/ExecutionEnvironmentContext/ExecutionEnvironmentContext';
import getNewTimeWithShift from '../helpers/getNewTimeWithShift';
import useCurrentTimeRef from './useCurrentTimeRef';

const usePlayerActions = ({
    id,
    playing,
    streamEntityType,
    totalTime,
    demoPlaybackStarted,
    selectSample,
    setPlaying,
    playStream,
}: {
    id: string | undefined;
    playing: boolean;
    streamEntityType: StreamEntityType;
    totalTime: number;
    demoPlaybackStarted: (id: string) => void;
    selectSample: (id: string) => void;
    setPlaying: (value: boolean) => void;
    playStream: (newTime?: number) => void;
}) => {
    const playingRef = useRef(playing);
    const interval =
        streamEntityType === StreamEntityType.Samples ? 3000 : 7000;
    const currentTimeRef = useCurrentTimeRef(id);
    const { isDAWPlugin } = useExecutionEnvironmentContext();
    const { pause: creatorPause, isInitialized: isCreatorInitialized } =
        useCreatorContext();
    const { setActivePlayerCallback, setActivePlayerId } =
        useActivePlayerContext();

    const _setPlaying = useCallback(
        async (playing: boolean, newTime?: number) => {
            const isPlayByPlugin = isDAWPlugin && !!id;
            const isSamples = streamEntityType === StreamEntityType.Samples;

            const isDemoPlayByPlugin = isPlayByPlugin && !isSamples;
            const isSamplesPlayByPlugin = isPlayByPlugin && isSamples;

            if (isSamplesPlayByPlugin) {
                selectSample(id);
                return;
            }

            if (isDemoPlayByPlugin) {
                demoPlaybackStarted(id);
            }

            if (playing) {
                playStream(newTime);
                isCreatorInitialized && creatorPause();
                setActivePlayerId(id);
                playingRef.current = true;
            } else {
                setPlaying(false);
                playingRef.current = false;
            }
        },
        [
            creatorPause,
            demoPlaybackStarted,
            id,
            isDAWPlugin,
            selectSample,
            setPlaying,
            streamEntityType,
            playStream,
            isCreatorInitialized,
            setActivePlayerId,
        ],
    );

    const playFromBeginning = useCallback(() => {
        _setPlaying(true, 0);
    }, [_setPlaying]);

    const playAnotherFragment = useCallback(
        (interval: number) => {
            const newTime = getNewTimeWithShift(
                interval,
                currentTimeRef.current,
                totalTime,
            );

            _setPlaying(true, newTime);
        },
        [_setPlaying, currentTimeRef, totalTime],
    );

    const playForward = useCallback(() => {
        playAnotherFragment(interval);
    }, [playAnotherFragment, interval]);

    const playRewind = useCallback(() => {
        playAnotherFragment(interval * -1);
    }, [playAnotherFragment, interval]);

    const togglePlaying = useCallback(() => {
        _setPlaying(!playingRef.current);
    }, [_setPlaying]);

    // When playing value is changed as a side effect, we need to keep the ref up to date
    useEffect(() => {
        playingRef.current = playing;
    }, [playing]);

    useEffect(() => {
        if (playing) {
            setActivePlayerCallback({
                playFromBeginning,
                playForward,
                playRewind,
                togglePlaying,
                stopPlaying: () => _setPlaying(false),
            });
        }
    }, [
        playing,
        setActivePlayerCallback,
        playFromBeginning,
        playForward,
        playRewind,
        togglePlaying,
        _setPlaying,
    ]);

    return {
        playFromBeginning,
        togglePlaying,
        playAnotherFragment,
    };
};

export default usePlayerActions;
